import { watchEloDisplayUpdates } from "./C1_eloDisplayUpdates";
import { eloSelectionOpacity, eloSelectionHoverHandlers } from "./C1_eloSelectionFocus";
import { resolvePlanDisplayMode } from "./C1_editModePresentation";
import type { C1_BaseObject } from "../model/C1_BaseObject";
import type { C1_Floor } from "../model/C1_Floor";
import type { C1_Opening } from "../model/C1_Opening";
import { planObjectKey } from "./C1_planObjectKey";
import c1_state from "../model/C1_State";
import type { C1_OpeningData } from "../types/C1_ModelTypes";
import { C1_EloItemView, prepareEloDisplayPositions, type C1_EloItemViewProps } from "./C1_EloItemView";
import { useModel } from "../model/C1_useModel";
import { useOnChangeWatcher } from "../../../src/models/useOnChangeWatcher";
import { C1_EloItem_Type } from "../types/C1_EloTypes";
import { furnitureById } from "../furniture/C1_furnitureRegistry";
import { FurnitureCategory } from "../types/C1_ModelTypes";
import { clientToPlan } from "./C1_planCoordinates";
import C1_EloItem from "../model/C1_EloItem";
import {
  isLightGroupDefinitionId,
  isSingleLightDefinitionId,
  syncLightGroup,
  syncSingleLight,
} from "../furniture/C1_lightGroup";
import { isElectricalFurnitureId } from "../furniture/C1_electricalFurniture";
import { getEloItemProfileForInstance } from "../data/C1_eloItemsRegistry";
import { getOpeningEloProfile } from "../data/C1_OpeningEloDefinitions";
import type Group from "../../../src/models/Group";
import { getMaterializedEloRoot } from "../model/C1_materializeEloDefinition";
import { Children, cloneElement, isValidElement, useEffect, useReducer, type MouseEvent, type ReactNode } from "react";
import type { C1_Room } from "../model/C1_Room";

type FurniturePreview = {
  roomId: string;
  furnitureIndex: number;
  x: number;
  y: number;
  rotation?: number;
  reflect?: boolean;
  wallDocked?: "L" | "R" | "M";
  width?: number;
  height?: number;
};

const openingRotation = { top: 0, right: 90, bottom: 180, left: 270 } as const;
function isElectricalFurniture(definitionId: string) {
  const definition = furnitureById[definitionId];
  return Boolean(
    definition &&
      (definition.category === FurnitureCategory.Electrical ||
        definition.additionalCategories?.includes(FurnitureCategory.Electrical) ||
        isElectricalFurnitureId(definitionId)),
  );
}

function isSelectedOwner(selection: C1_BaseObject<any> | undefined, object: C1_BaseObject<any>): boolean {
  return Boolean(selection && selection.className !== "C1_EloItem" && selection === object);
}

export function C1_EloItemsSvgView({
  floor,
  openings,
  furniturePreview,
  openingPreview,
  onClick,
  onDoubleClick,
  onRoomDoubleClick,
  showGhosts = true,
}: {
  floor: C1_Floor;
  openings: C1_Opening[];
  furniturePreview?: FurniturePreview;
  openingPreview?: C1_OpeningData;
  onClick?: (object: C1_BaseObject<any> | undefined) => void;
  onDoubleClick?: (object: C1_BaseObject<any> | undefined) => void;
  onRoomDoubleClick?: (room: C1_Room, clientPoint: { x: number; y: number }) => void;
  showGhosts?: boolean;
}) {
  useModel(c1_state);
  const [, refreshDisplayPositions] = useReducer(value => value + 1, 0);
  useEffect(() => watchEloDisplayUpdates(refreshDisplayPositions), []);
  useOnChangeWatcher(c1_state.settings);
  useOnChangeWatcher(c1_state.project.displayMode);
  const selection = c1_state.selection;
  const renderingPdf = c1_state.floorImageRenderOptions.value !== null;
  if (!renderingPdf && (c1_state.editMode.value === "furnishing" || c1_state.editMode.value === "building")) return null;
  const displayMode = resolvePlanDisplayMode(state.settings.applicationMode, c1_state.editMode.value, c1_state.project.displayMode.value, c1_state.floorImageRenderOptions.value?.displayMode);
  const technicalMode = displayMode !== "design";
  const electricalMode = (c1_state.editMode.value === "EloConcept" || c1_state.editMode.value === "EloDetail") || c1_state.editMode.value === "interactionConcept";
  const showWindowConsumers = technicalMode || (!renderingPdf && electricalMode);

  if (!floor) return null;

  // Electrical symbols are siblings of the room hit areas, so bubbling cannot reach those areas.
  const zoomRoom = (room: C1_Room) => (event: MouseEvent<SVGGElement>) => {
    if (renderingPdf || !onRoomDoubleClick) return;
    event.preventDefault();
    event.stopPropagation();
    onRoomDoubleClick(room, { x: event.clientX, y: event.clientY });
  };

  const displayedOpenings = openings;

  const content = (
    <g
      className={technicalMode ? "C1_technicalEloItems" : undefined}
      data-c1-svg-layer="elo-items"
      data-c1-display-mode={displayMode}
    >
      {floor.rooms.map((room) => {
        const eloItemRoot = getMaterializedEloRoot(room.owner, "room");
        if (!eloItemRoot) return null;
        const rect = room.rect();
        return (
          <g opacity={eloSelectionOpacity(room, c1_state)} {...eloSelectionHoverHandlers(room, c1_state)} key={`room-elo-${planObjectKey(room)}`} data-c1-room-id={room.id} onDoubleClick={zoomRoom(room)}>
            <C1_EloItemView
              displayPositionsPrepared
              showGhosts={showGhosts}
              eloItem={eloItemRoot}
              origin={room.automaticPlacementPoint()}
              ownerSelected={!renderingPdf && isSelectedOwner(selection, room)}
              onClick={onClick}
              onDoubleClick={onDoubleClick}
              placementProps={{
                SX: rect.x2 - rect.x1,
                SY: rect.y2 - rect.y1,
                sx: rect.x2 - rect.x1,
                sy: rect.y2 - rect.y1,
              }}
            />
          </g>
        );
      })}

      {displayedOpenings.map((opening) => {
        if (opening.kind === "window" && !showWindowConsumers) return null;
        const eloItemRoot = getMaterializedEloRoot(
          opening.owner as Group | undefined,
          getOpeningEloProfile(opening).rootDefinitionId,
        );
        if (!eloItemRoot) return null;
        // A room plan also includes shared doors owned by rooms outside its restricted floor view.
        const room = opening.room ?? floor.rooms.find((candidate) => candidate.id === opening.roomId);
        if (!room) return null;
        const center = opening.absolutePosition;
        const thicknessM = (opening.wall ? room.isExteriorSegment(opening.wall) : true)
          ? c1_state.project.settings.wallThicknessExterior
          : c1_state.project.settings.wallThicknessInterior;
        return (
          <g opacity={eloSelectionOpacity(opening, c1_state)} {...eloSelectionHoverHandlers(opening, c1_state)} key={`opening-elo-${planObjectKey(opening)}`} data-c1-room-id={room.id} data-c1-opening-id={opening.id} onDoubleClick={zoomRoom(room)}>
            <C1_EloItemView
              displayPositionsPrepared
              showGhosts={showGhosts}
              eloItem={eloItemRoot}
              origin={center}
              ownerSelected={!renderingPdf && isSelectedOwner(selection, opening)}
              rotation={openingRotation[opening.side]}
              collisionWallRotation={openingRotation[opening.side]}
              exteriorLightRotation={openingRotation[opening.side] + 180}
              reflect={opening.kind === "door" ? opening.reflect : undefined}
              uprightItemTypes={opening.kind === "door" ? [C1_EloItem_Type.Button] : undefined}
              onClick={onClick}
              onDoubleClick={onDoubleClick}
              placementProps={{
                SX: opening.width,
                WT: thicknessM,
                DStation: 0.3,
                DButton: 0.1,
                DPlug: 0.1,
                DLight: 0.3,
                DPaper: technicalMode ? 0.3 : 0.5,
              }}
              ghostDefinitions={getOpeningEloProfile(opening).definitions}
            />
          </g>
        );
      })}

      {floor.rooms.flatMap((room) => {
        const placementRect = room.innerRect();
        return room.furnitures.flatMap((furniture) => {
          const furnitureIndex = room.furnitures.indexOf(furniture);
          const preview =
            furniturePreview?.roomId === room.id && furniturePreview.furnitureIndex === furnitureIndex
              ? furniturePreview
              : undefined;
          const relativePosition = preview ? { x: preview.x, y: preview.y } : furniture.relativePosition;
          const equipmentOrigin = preview ? undefined : c1_state.project.equipmentPostitionGeometric(furniture.id);
          const modelX =
            equipmentOrigin?.x ?? placementRect.x1 + relativePosition.x * (placementRect.x2 - placementRect.x1);
          const modelY =
            equipmentOrigin?.y ?? placementRect.y1 + relativePosition.y * (placementRect.y2 - placementRect.y1);
          const previewBaseProps = {
            ...furniture.baseProps,
            ...(preview?.rotation !== undefined ? { rotation: preview.rotation } : {}),
            ...(preview?.width !== undefined ? { width: preview.width } : {}),
            ...(preview?.height !== undefined ? { height: preview.height } : {}),
          };
          const synchronization =
            preview && isLightGroupDefinitionId(furniture.definitionId)
              ? syncLightGroup(
                  previewBaseProps,
                  furniture.eloTree,
                  (preview.wallDocked ?? furniture.wallDocked) !== undefined,
                  furniture.definitionId,
                )
              : preview && isSingleLightDefinitionId(furniture.definitionId)
                ? syncSingleLight(
                    previewBaseProps,
                    furniture.eloTree,
                    (preview.wallDocked ?? furniture.wallDocked) !== undefined,
                    furniture.definitionId,
                  )
                : { props: previewBaseProps, eloTree: furniture.eloTree };
          const baseProps = synchronization.props;
          const eloItemRoot = getMaterializedEloRoot(
            furniture.owner as Group | undefined,
            synchronization.eloTree,
          );
          if (!eloItemRoot) return [];
          const placementProps = Object.fromEntries(
            Object.entries(baseProps).filter((entry): entry is [string, number] => typeof entry[1] === "number"),
          );
          placementProps.SX = baseProps.width;
          placementProps.SY = baseProps.height;
          placementProps.sx = baseProps.width;
          placementProps.sy = baseProps.height;

          return [
            <g opacity={eloSelectionOpacity(furniture, c1_state)} {...eloSelectionHoverHandlers(furniture, c1_state)} key={`furniture-elo-${planObjectKey(furniture)}`} data-c1-room-id={room.id} onDoubleClick={zoomRoom(room)}>
              <C1_EloItemView
                displayPositionsPrepared
                showGhosts={showGhosts}
                eloItem={eloItemRoot}
                origin={{ x: modelX, y: modelY }}
                ownerSelected={!renderingPdf && isSelectedOwner(selection, furniture)}
                rotation={preview?.rotation ?? furniture.absoluteRotation}
                wallDockSide={(preview ? preview.wallDocked : furniture.wallDocked) !== undefined ? furniture.side : undefined}
                reflect={preview?.reflect ?? furniture.absoluteReflect}
                uprightItemTypes={[C1_EloItem_Type.Light, C1_EloItem_Type.Button, C1_EloItem_Type.Plug]}
                placementProps={placementProps}
                ghostDefinitions={getEloItemProfileForInstance(furniture.definitionId, synchronization.eloTree)}
                onClick={onClick}
                onDoubleClick={onDoubleClick}
                onStartOwnerFurnitureDrag={
                  isElectricalFurniture(furniture.definitionId)
                    ? (event) => {
                        const currentView = c1_state.viewFromBounds();
                        const pointer = clientToPlan(
                          event.currentTarget.closest(".C1_canvas"),
                          event.clientX,
                          event.clientY,
                          currentView,
                        );
                        if (!pointer) return false;
                        c1_state.setSelection(furniture);
                        c1_state.dragView.value = currentView;
                        c1_state.drag.value = {
                          type: "furniture",
                          roomId: room.id,
                          furnitureIndex,
                          startX: pointer.x,
                          startY: pointer.y,
                          startRelativePosition: { ...furniture.relativePosition },
                          roomRect: placementRect,
                          furnitureSize: {
                            width: furniture.baseProps.width,
                            height: furniture.baseProps.height,
                          },
                          startSide: furniture.side,
                          startReflect: furniture.reflect,
                          view: currentView,
                        };
                        return true;
                      }
                    : undefined
                }
              />
            </g>,
          ];
        });
      })}
    </g>
  );
  const views: C1_EloItemViewProps[] = [];
  const collect = (nodes: ReactNode) => Children.forEach(nodes, node => {
    if (!isValidElement<{children?: ReactNode}>(node)) return;
    if (node.type === C1_EloItemView) views.push(node.props as unknown as C1_EloItemViewProps);
    else collect(node.props.children);
  });
  collect(content);
  const ghosts = prepareEloDisplayPositions(views, displayMode);
  const prepare = (node: ReactNode): ReactNode => {
    if (Array.isArray(node)) return node.map(prepare);
    if (!isValidElement<{children?: ReactNode}>(node)) return node;
    if (node.type === C1_EloItemView) {
      const view = node as unknown as React.ReactElement<C1_EloItemViewProps>;
      return cloneElement(view, {preparedGhostItems: ghosts.get(view.props), onLightImageBoundsLoaded: refreshDisplayPositions});
    }
    return node.props.children === undefined ? node : cloneElement(node, undefined, prepare(node.props.children));
  };
  return prepare(content) as JSX.Element;
}
